import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { delAuth } from '@/api/ApiCalls';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import type { NedarimDecodeEntryDtoIn } from '@/models/in/NedarimDecodeModels';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Button from '@/components/ui/Button';
import { DECODE_ENTRIES_URL } from './nedarimTransactionsFundAllocation';
import styles from './fundAllocationTabs.module.scss';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  rule: NedarimDecodeEntryDtoIn;
}

const DeleteDecodeRuleDialog = ({ open, onClose, onSuccess, rule }: Props) => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const headers = { Authorization: `Bearer ${currentUser?.token}` };
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const remove = async () => {
    setError(null);
    setIsDeleting(true);
    try {
      await delAuth(`${DECODE_ENTRIES_URL}/${rule.id}`, headers);
      onSuccess();
      onClose();
    } catch {
      setError(t('nedarimTransactionsFundAllocation.tabs.rulesDeleteFailed'));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <MainDialogLayout open={open} onOpenChange={(value: boolean) => !value && onClose()} title={t('nedarimTransactionsFundAllocation.tabs.deleteRule')}>
      <p className="body-md-regular">{t('nedarimTransactionsFundAllocation.tabs.deleteRuleConfirm', { source: rule.sourceValue })}</p>

      {error && <p className={styles.formError}>{error}</p>}

      <div className={styles.dialogFooter}>
        <Button variant="white" onClick={onClose} disabled={isDeleting}>
          {t('cancel')}
        </Button>
        <Button variant="primary" className="!bg-red-500" onClick={remove} disabled={isDeleting}>
          {isDeleting ? t('nedarimTransactionsFundAllocation.tabs.deleting') : t('nedarimTransactionsFundAllocation.tabs.delete')}
        </Button>
      </div>
    </MainDialogLayout>
  );
};

export default DeleteDecodeRuleDialog;
